// Field + column schemas that drive the generic admin ResourceManager.
// types: text | number | textarea | boolean | select | image | tags | lines

export const SCHEMAS = {
  products: {
    singular: "product",
    idField: "id",
    hasGallery: true,
    columns: [
      { key: "image", type: "image" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "price", label: "Price", type: "price", quickEdit: true },
      { key: "role", label: "Role", type: "badge" },
      { key: "inventory", label: "Stock", quickEdit: "number" },
      { key: "status", label: "Status", type: "status" },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "price", label: "Price (\u20B9)", type: "number", required: true },
      { key: "compare_at", label: "Compare-at price (\u20B9)", type: "number" },
      { key: "role", label: "Role", type: "select", options: ["centerpiece", "accessory", "founder"] },
      { key: "description", label: "Description", type: "textarea" },
      { key: "intent_tags", label: "Collections", type: "collections", hint: "Pick every collection this product belongs to — one product can be in many." },
      { key: "tags", label: "Tags", type: "tags" },
      { key: "colors", label: "Colors", type: "tags" },
      { key: "inventory", label: "Inventory", type: "number" },
      { key: "featured", label: "Featured", type: "boolean" },
      { key: "best_seller", label: "Best seller", type: "boolean" },
      { key: "personalizable", label: "Personalizable", type: "boolean" },
      { key: "is_sold_out", label: "Sold out", type: "boolean" },
    ],
    defaults: { role: "accessory", inventory: 100, personalizable: false, is_sold_out: false, rating: 4.8, reviews_count: 0, image: "", images: [], intent_tags: [], tags: [], colors: [], featured: false, best_seller: false, hidden: false, archived: false },
  },

  parcels: {
    singular: "parcel",
    idField: "id",
    hasGallery: true,
    columns: [
      { key: "image", type: "image" },
      { key: "name", label: "Name" },
      { key: "price", label: "Price", type: "price", quickEdit: true },
      { key: "badge", label: "Badge", type: "badge" },
      { key: "status", label: "Status", type: "status" },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true, hint: "url-friendly, e.g. birthday-bloom-box" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "price", label: "Price (\u20B9)", type: "number", required: true },
      { key: "compare_at", label: "Compare-at price (\u20B9)", type: "number" },
      { key: "badge", label: "Badge", type: "text", hint: "e.g. Bestseller" },
      { key: "occasion", label: "Occasion", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "hero_product_id", label: "Hero product id", type: "text" },
      { key: "item_ids", label: "Included product ids", type: "tags" },
      { key: "collections", label: "Collections", type: "collections", hint: "Pick the collections this parcel belongs to." },
      { key: "includes", label: "What's inside (one per line)", type: "lines" },
      { key: "featured", label: "Featured", type: "boolean" },
      { key: "best_seller", label: "Best seller", type: "boolean" },
      { key: "personalizable", label: "Personalizable", type: "boolean" },
    ],
    defaults: { personalizable: true, rating: 4.9, reviews_count: 0, image: "", images: [], collections: [], item_ids: [], includes: [], featured: false, best_seller: false, hidden: false, archived: false },
  },

  collections: {
    singular: "collection",
    idField: "slug",
    columns: [
      { key: "image", type: "image" },
      { key: "name", label: "Name" },
      { key: "slug", label: "Slug" },
      { key: "type", label: "Type", type: "badge" },
      { key: "priority", label: "Priority", quickEdit: "number" },
      { key: "status", label: "Status", type: "status" },
    ],
    fields: [
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "emoji", label: "Emoji", type: "text" },
      { key: "image", label: "Image", type: "image" },
      { key: "accent", label: "Accent", type: "text" },
      { key: "type", label: "Type", type: "select", options: ["intent", "budget", "founder", "build", "editorial"] },
      { key: "priority", label: "Priority", type: "number" },
    ],
    defaults: { type: "intent", priority: 10, hidden: false, archived: false },
  },

  vision: {
    singular: "initiative",
    idField: "slug",
    columns: [
      { key: "image", type: "image" },
      { key: "title", label: "Title" },
      { key: "slug", label: "Slug" },
    ],
    fields: [
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "icon", label: "Icon (lucide name)", type: "text", hint: "e.g. briefcase" },
      { key: "image", label: "Image", type: "image" },
    ],
    defaults: {},
  },

  reviews: {
    singular: "review",
    idField: "id",
    columns: [
      { key: "avatar", type: "image" },
      { key: "name", label: "Name" },
      { key: "parcel", label: "Parcel" },
      { key: "rating", label: "Rating" },
      { key: "approved", label: "Approved", type: "boolean" },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "city", label: "City", type: "text" },
      { key: "rating", label: "Rating (1-5)", type: "number" },
      { key: "parcel", label: "Parcel", type: "text" },
      { key: "text", label: "Review", type: "textarea", required: true },
      { key: "avatar", label: "Avatar image", type: "image" },
      { key: "approved", label: "Approved", type: "boolean" },
    ],
    defaults: { rating: 5, approved: true },
  },

  faqs: {
    singular: "FAQ",
    idField: "id",
    columns: [
      { key: "q", label: "Question" },
      { key: "approved", label: "Approved", type: "boolean" },
    ],
    fields: [
      { key: "q", label: "Question", type: "text", required: true },
      { key: "a", label: "Answer", type: "textarea", required: true },
      { key: "approved", label: "Approved", type: "boolean" },
    ],
    defaults: { approved: true },
  },
};

// Extra fields shown only in Founder mode (nested under `limited`).
export const FOUNDER_FIELDS = [
  { key: "limited.is_limited", label: "Limited edition", type: "boolean" },
  { key: "limited.hand_numbered", label: "Hand-numbered", type: "boolean" },
  { key: "limited.batch_label", label: "Batch label", type: "text", hint: "e.g. Batch No. 1" },
  { key: "limited.next_batch_message", label: "Sold-out message", type: "text", hint: "e.g. Next batch coming soon" },
];

export function getSchema(resource, founder) {
  const base = SCHEMAS[resource];
  if (!founder) return base;
  return {
    ...base,
    fields: [...base.fields, ...FOUNDER_FIELDS],
    defaults: {
      ...base.defaults,
      role: "founder",
      is_sold_out: false,
      inventory: 8,
      limited: { is_limited: true, hand_numbered: true, batch_label: "", next_batch_message: "Next batch coming soon" },
    },
  };
}
